/**
 * The focus trap behind `Modal`, kept separate so it can be tested as
 * plain functions rather than only through a rendered dialog.
 *
 * Why hand-rolled instead of `<dialog showModal()>`, which gets the trap,
 * the scrim and the Escape handling from the platform for free: jsdom 29
 * — the environment this suite runs in — ships `<dialog>` with no
 * `showModal` at all (verified: `'showModal' in dialogElement` is false).
 * Using the native dialog would mean the modal's keyboard behaviour is
 * exactly the part of the app that can never be covered by a test, on a
 * component whose whole reason for existing is that keyboard behaviour.
 */

/**
 * Everything that can take focus. Deliberately a static selector rather
 * than a visibility check: `offsetParent` and `getClientRects()` both
 * report "invisible" for every element in jsdom, so a visibility filter
 * would make the trap untestable and, worse, make it silently do nothing.
 * A modal's subtree is small and fully visible by construction.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** The focusable descendants of `container`, in tab order. */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((element) => !element.hasAttribute('hidden'))
}

/**
 * The slice of a keyboard event the trap needs. Structural rather than
 * `KeyboardEvent`, so the same function serves React's synthetic event
 * (which is what `Modal` hands it) and a plain object in a unit test.
 */
export interface TabKeyEvent {
  shiftKey: boolean
  preventDefault: () => void
}

/**
 * Handles one Tab / Shift+Tab inside `container`, wrapping at the ends.
 *
 * Call it only for `event.key === 'Tab'`; it calls `preventDefault()`
 * whenever it moves focus itself and leaves the event alone otherwise, so
 * ordinary Tab steps between the dialog's own controls stay native.
 *
 * The third case — focus is in the container but not on any focusable
 * element, which is where the dialog starts, since it opens with focus on
 * its own `tabIndex={-1}` wrapper so a screen reader reads the title
 * first — is treated as "before the first element": Tab goes to the
 * first, Shift+Tab to the last.
 */
export function trapTabKey(container: HTMLElement, event: TabKeyEvent): void {
  const focusable = getFocusableElements(container)
  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (first === undefined || last === undefined) {
    // Nothing to tab to. Keep focus on the container rather than letting
    // it escape to the page behind the scrim.
    event.preventDefault()
    container.focus()
    return
  }

  const active = document.activeElement
  const index = focusable.findIndex((element) => element === active)

  if (index === -1) {
    event.preventDefault()
    ;(event.shiftKey ? last : first).focus()
    return
  }
  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
    return
  }
  if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}
